import { Box, Typography } from '@material-ui/core';
import { Layout } from '@roc/feature-app-core';
import { loadPlatformWebComponents } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { DocumentLibraryContentfulPage } from './documentLibraryContentfulPage';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'platform-ui': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        id: string;
        component: string;
        [key: string]: any;
      };
    }
  }
}

export enum DocumentLibraryName {
  SPL = 'SPL',
  INTERNAL = 'INTERNAL',
}

enum DocumentLibraryView {
  LIBRARY = 'library',
  FOLDERS = 'folders',
  VIEWER = 'viewer',
}

interface ContentfulPageRequest {
  documentId: string;
  contentId?: string;
}

interface DocumentLibraryPlatformUiProps {
  library?: DocumentLibraryName;
  breadcrumbParent?: string;
  manage?: boolean;
  hideVisibility?: boolean;
  /**
   * Reads the library as the selected lender's company sees it. Internal
   * portals only: the backend ignores it for every other caller.
   */
  lenderId?: number;
}

export const DocumentLibraryPlatformUi = observer(
  ({
    library = DocumentLibraryName.SPL,
    breadcrumbParent = 'Resources',
    manage = false,
    hideVisibility = true,
    lenderId,
  }: DocumentLibraryPlatformUiProps) => {
    const hostRef = useRef<HTMLElement>(null);
    const [view, setView] = useState<DocumentLibraryView>(
      DocumentLibraryView.LIBRARY
    );
    const [documentId, setDocumentId] = useState<string>();
    const [contentfulPage, setContentfulPage] = useState<ContentfulPageRequest>();
    const [unavailable, setUnavailable] = useState(false);

    useEffect(() => {
      let mounted = true;
      loadPlatformWebComponents().then(() => {
        if (mounted) {
          document
            .getElementById('platform-wc-styles')
            ?.removeAttribute('disabled');
        }
      });
      return () => {
        mounted = false;
        document
          .getElementById('platform-wc-styles')
          ?.setAttribute('disabled', 'true');
      };
    }, []);

    useEffect(() => {
      const node = hostRef.current;
      if (!node) {
        return;
      }
      const handler = (event: Event) => {
        const { eventName, payload } = (event as CustomEvent).detail ?? {};
        switch (eventName) {
          case 'onViewDocument':
            setContentfulPage(undefined);
            setDocumentId(String(payload?.documentId));
            setView(DocumentLibraryView.VIEWER);
            break;
          case 'onBackToLibrary':
            setContentfulPage(undefined);
            setDocumentId(undefined);
            setView(DocumentLibraryView.LIBRARY);
            break;
          case 'onManageFolders':
            setContentfulPage(undefined);
            setView(DocumentLibraryView.FOLDERS);
            break;
          case 'onContentfulPageRequested':
            setContentfulPage({
              documentId: String(payload?.documentId),
              contentId: payload?.contentId,
            });
            break;
          case 'onDocumentLibraryUnavailable':
            setUnavailable(true);
            break;
        }
      };
      node.addEventListener('platform-ui-event', handler);
      return () => node.removeEventListener('platform-ui-event', handler);
    }, [unavailable]);

    return (
      <Layout maxWidth={'xl'}>
        {unavailable ? (
          <Box p={4} textAlign="center" bgcolor="#fff" borderRadius={8}>
            <Typography variant="h6">
              The Document Library is unavailable
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please try again in a few minutes.
            </Typography>
          </Box>
        ) : (
          <platform-ui
            ref={hostRef}
            id="platform-ui-document-library"
            component="document_library"
            data-library={library}
            data-view={view}
            data-document-id={documentId}
            data-breadcrumb-parent={breadcrumbParent}
            data-manage={manage ? 'true' : undefined}
            data-hide-visibility={hideVisibility ? 'true' : undefined}
            data-lender-id={lenderId == null ? undefined : String(lenderId)}
          >
            {contentfulPage && (
              <div slot="contentful-page">
                <DocumentLibraryContentfulPage
                  documentId={contentfulPage.documentId}
                  contentId={contentfulPage.contentId}
                />
              </div>
            )}
          </platform-ui>
        )}
      </Layout>
    );
  }
);
